/**
 * Rule that checks whether gateways are converged onto a join
 */
const { is } = require("bpmnlint-utils");

function isParallel(node) {
  return is(node, "bpmn:ParallelGateway");
}

function isInclusive(node) {
  return is(node, "bpmn:InclusiveGateway");
}

function isBpmnNode(node) {
  if (!node) return false;
  return !!node.$type;
}

function isJoin(node) {
  if (!isBpmnNode(node)) return false;
  const incoming = Array.isArray(node.incoming) ? node.incoming : [];
  return incoming.length > 1;
}

function scanForMatchingJoins(startNode, isSameType) {
  const joins = new Set();
  const queue = [];
  const visited = new Set();

  if (!isBpmnNode(startNode)) {
    return joins;
  }

  queue.push(startNode);

  while (queue.length > 0) {
    const cur = queue.shift();
    if (!cur || !cur.id) continue;
    if (visited.has(cur.id)) continue;
    visited.add(cur.id);

    if (!isBpmnNode(cur)) continue;

    if (isSameType(cur) && isJoin(cur)) {
      joins.add(cur);
      continue;
    }

    const outgoing = Array.isArray(cur.outgoing) ? cur.outgoing : [];
    for (const flow of outgoing) {
      if (!flow || !flow.targetRef) continue;
      const next = flow.targetRef;
      if (!isBpmnNode(next)) continue;
      queue.push(next);
    }
  }

  return joins;
}

function isJoinOfUpstreamSameTypeSplit(joinNode, isSameType) {
  if (!isBpmnNode(joinNode)) return false;
  if (!isJoin(joinNode)) return false;

  const incoming = Array.isArray(joinNode.incoming) ? joinNode.incoming : [];
  if (!incoming.length) return false;

  const q = [];
  const visited = new Set();

  for (const f of incoming) {
    const src = f && f.sourceRef;
    if (isBpmnNode(src)) q.push(src);
  }

  while (q.length) {
    const cur = q.shift();
    if (!cur || !cur.id) continue;
    if (visited.has(cur.id)) continue;
    visited.add(cur.id);

    if (isSameType(cur)) {
      const out = Array.isArray(cur.outgoing) ? cur.outgoing : [];
      if (out.length >= 2) {
        let ok = true;
        for (const flow of out) {
          const next = flow && flow.targetRef;
          if (!isBpmnNode(next)) {
            ok = false;
            break;
          }
          const joins = scanForMatchingJoins(next, isSameType);
          if (!joins.has(joinNode)) {
            ok = false;
            break;
          }
        }
        if (ok) return true;
      }
    }

    const inc = Array.isArray(cur.incoming) ? cur.incoming : [];
    for (const f of inc) {
      const src = f && f.sourceRef;
      if (isBpmnNode(src)) q.push(src);
    }
  }

  return false;
}

function intersect(sets) {
  if (!sets.length) return new Set();
  const result = new Set(sets[0]);

  for (const s of sets.slice(1)) {
    for (const item of Array.from(result)) {
      if (!s.has(item)) result.delete(item);
    }
  }

  return result;
}

module.exports = function gatewayJoinMatching() {
  return {
    check(node, reporter) {
      if (!isBpmnNode(node)) return;

      const parallel = isParallel(node);
      const inclusive = isInclusive(node);
      if (!parallel && !inclusive) return;

      const outgoing = Array.isArray(node.outgoing) ? node.outgoing : [];

      const isSameType = parallel ? isParallel : inclusive ? isInclusive : () => false;

      if (isJoin(node) && isJoinOfUpstreamSameTypeSplit(node, isSameType)) {
        return;
      }

      if (outgoing.length === 0) {
        if (parallel) reporter.report(node.id, "Diverging Parallel Gateway must have at least one outgoing path.");
        if (inclusive) reporter.report(node.id, "Diverging Inclusive Gateway must have at least one outgoing path.");
        return;
      }

      const branchJoins = [];
      for (const flow of outgoing) {
        if (!flow || !flow.targetRef) continue;
        const next = flow.targetRef;

        if (!isBpmnNode(next)) {
          branchJoins.push(new Set());
          continue;
        }

        branchJoins.push(scanForMatchingJoins(next, isSameType));
      }

      const common = intersect(branchJoins);

      if (!common.size) {
        if (parallel) reporter.report(node.id, "Parallel Gateway paths must converge on a Parallel Gateway join.");
        if (inclusive) reporter.report(node.id, "Inclusive Gateway paths must converge on an Inclusive Gateway join.");
      }
    }
  };
};
